/**
 * @file index.tsx
 *
 * @fileoverview Renders job cart meta data.
 */
import { useQuery } from '@apollo/client';
import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { getCartMetaDataQuery } from '~/jobiqo/components/Checkout/graphql/queries/getCartMetaData';
import { JobWizardFlow } from '~/jobiqo/components/Job/Wizard/config';
import { useBundleIdParam } from '~/jobiqo/components/Job/Wizard/hooks';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import Price from '~/jobiqo/models/price';
import { Credit, Job } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Hr } from '~/vcl/atoms/06-layout/10-hr';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { useConfig } from '~/jobiqo/context/config';
import { hasFlatRate } from './utils';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface JobCardMetadataProps {
  /**
   * The current job wizard flow (e.g. republish or adding upgrades).
   *
   * @default JobWizardFlow.New
   */
  flow?: JobWizardFlow;
  /**
   * The job if the cart is for a specific job (republish or upgrades).
   */
  job?: Job;
}

export const JobCartMetaData = ({
  flow = JobWizardFlow.New,
  job,
}: JobCardMetadataProps) => {
  const userContext: UserContextInterface = useContext(UserContext);
  const { JOBS } = useFrontendConfig();
  const SHOW_DESTINATION_STEP = JOBS.JOB_POSTING_WIZARD_SHOW_DESTINATION;
  const { getConfigByName } = useConfig();
  const SUBSCRIPTIONS_FEATURE_ACTIVE: boolean = !!getConfigByName(
    'jobiqo_subscription',
  )?.value[0];
  const bundleId = useBundleIdParam(job && job.id);

  const { data, error } = useQuery(
    getCartMetaDataQuery(SUBSCRIPTIONS_FEATURE_ACTIVE),
    {
      fetchPolicy: 'no-cache',
      variables: {
        organizationProfileId: userContext?.currentOrganizationProfile?.id,
        defaultProducts: bundleId ? [bundleId] : [],
      },
    },
  );

  // Do not show any message if error happens.
  if (error) {
    return <Spacing size="medium" />;
  }

  let DefaultProductPrice: Price | null = null;

  if (data?.getCartMetaData?.defaultProduct) {
    DefaultProductPrice = new Price(
      data?.getCartMetaData?.defaultProduct?.priceNumber,
      data?.getCartMetaData?.defaultProduct?.priceCurrency,
    );
  }

  const credits = data?.getCartMetaData?.credits;
  const jobSlots =
    data?.getCartMetaData?.flatrateSubscription?.availableJobSlots;
  let productHasFlatRate = hasFlatRate(
    data?.getCartMetaData,
    data?.getCartMetaData?.defaultProduct?.excludeJobFlatrateSubscription,
  );

  // Check if the default product has a flat rate for the frontend.
  // When the subscription does not have any FE it means it is valid for
  // all frontends, so we should also check for that.
  let hasFlatrateForFrontend = false;
  if (productHasFlatRate && SHOW_DESTINATION_STEP) {
    hasFlatrateForFrontend =
      data?.getCartMetaData?.defaultProduct?.frontends?.some((frontend) =>
        data?.getCartMetaData?.activeJobFlatrateSubscriptions?.some(
          (subscription) =>
            subscription?.frontend === frontend ||
            subscription?.frontend === null,
        ),
      ) || false;

    productHasFlatRate = hasFlatrateForFrontend;
  }

  return (
    <>
      {productHasFlatRate && (
        <>
          <Text color="gray8">
            <FormattedMessage
              id="cart_meta_data_flatrate_subscription"
              defaultMessage="Flat rate will be used"
            />
          </Text>
          <Spacing />
        </>
      )}

      {flow !== JobWizardFlow.Upgrades ? (
        <>
          {DefaultProductPrice && (
            <>
              <Hr />
              <Spacing />
              <FlexItems justifyContent="space-between">
                <FlexItem>
                  <Text margin={0} color="gray9">
                    {data.getCartMetaData.defaultProduct.name}
                  </Text>
                </FlexItem>
                <FlexItem>
                  <Text margin={0} color="gray5">
                    {data?.getCartMetaData?.defaultProductUserCredits?.amount &&
                    !data?.getCartMetaData?.flatrateSubscription ? (
                      data?.getCartMetaData?.defaultProductUserCredits?.amount >
                      1 ? (
                        <FormattedMessage
                          id="cart_meta_default_product_price_free_plural"
                          defaultMessage="{amount} job credits"
                          values={{
                            amount:
                              data?.getCartMetaData.defaultProductUserCredits
                                .amount,
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="cart_meta_default_product_price_free_singular"
                          defaultMessage="{amount} job credit"
                          values={{
                            amount:
                              data?.getCartMetaData.defaultProductUserCredits
                                .amount,
                          }}
                        />
                      )
                    ) : (
                      <span
                        style={{
                          textDecoration: productHasFlatRate
                            ? 'line-through'
                            : 'none',
                        }}
                      >
                        {DefaultProductPrice.getPriceRepresentation()}
                      </span>
                    )}
                  </Text>
                </FlexItem>
              </FlexItems>
              <Spacing size="medium" />
              <Hr />
            </>
          )}
        </>
      ) : (
        <FlexItems alignItems="center" gap={4}>
          {job && job.title}
        </FlexItems>
      )}

      {credits && (
        <>
          <Spacing />
          <strong>
            <Text tag="span" margin={0} color="gray8">
              <FormattedMessage
                id="cart_meta_available_credits"
                defaultMessage="Available credits"
              />
            </Text>
          </strong>{' '}
          : {credits.reduce((total, { amount }: Credit) => total + amount, 0)}
          <Spacing marginLeft={4} marginTop={0}>
            <ul>
              {credits.map(
                (
                  { label, amount, organizationName, fromParentOrg }: Credit,
                  key: number,
                ) =>
                  !!amount && (
                    <li key={key}>
                      {label} {fromParentOrg ? `(${organizationName})` : ''}:{' '}
                      {amount}
                    </li>
                  ),
              )}
            </ul>
          </Spacing>
        </>
      )}
      <Spacing size="medium" />
      {data?.getCartMetaData?.flatrateSubscription && jobSlots !== null && (
        <>
          <Spacing />
          <strong>
            <Text tag="span" margin={0} color="gray8">
              <FormattedMessage
                id="cart_meta_available_job_slots"
                defaultMessage="Available job slots"
              />
            </Text>
          </strong>{' '}
          : {jobSlots}
        </>
      )}
      <Spacing size="medium" />
    </>
  );
};
